import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import cn from "classnames";
import { Calendar, Camera, Flag, Message2, Star1 } from "iconsax-react";
import { Avatar } from "@/shared/ui";
import type { TaskListItem } from "../types";
import { PRIORITY_MAP, PRIORITY_COLOR_CLASS } from "../consts";
import { generateTaskCode, getDueDateDisplay, getTagClasses } from "../utils";

interface Props {
  task: TaskListItem;
  projectName: string;
  isDragOverlay?: boolean;
  isBacklog?: boolean;
  canDragAll?: boolean;
  currentUserId?: number | null;
  onTaskClick?: (task: TaskListItem) => void;
  onQuickMove?: (task: TaskListItem) => void;
  onToggleFavorite?: () => void;
}

export default function KanbanTaskCard({
  task,
  projectName,
  isDragOverlay = false,
  canDragAll,
  currentUserId,
  onTaskClick,
}: Props) {
  const { t } = useTranslation("TasksPage");

  const canDrag = canDragAll || task.assigned_to === currentUserId;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: isDragOverlay || !canDrag,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  function handleClick() {
    if (!isDragging && onTaskClick) {
      onTaskClick(task);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onTaskClick?.(task);
    }
  }

  const taskCode = generateTaskCode(projectName, task.id);
  const dueDateDisplay = getDueDateDisplay(task.due_date);
  const dueDateColorClass = dueDateDisplay.isUrgent ? "content-action-negative" : "content-action-neutral";

  return (
    <div
      ref={!isDragOverlay ? setNodeRef : undefined}
      style={!isDragOverlay ? style : undefined}
      {...(!isDragOverlay ? attributes : {})}
      {...(!isDragOverlay ? listeners : {})}
      onClick={handleClick}
      onKeyDown={onTaskClick ? handleKeyDown : undefined}
      role={onTaskClick ? "button" : undefined}
      tabIndex={onTaskClick ? 0 : undefined}
      className={cn(
        "group h-[136px] p-3 radius-md border surface-component-stroke surface-base-fill",
        isDragOverlay || !canDrag ? "cursor-pointer" : "cursor-grab",
        isDragging && "opacity-30",
        isDragOverlay && "shadow-lg",
      )}>
      <div className="flex flex-col h-full">
        <p className="text-label-sm content-base-primary line-clamp-3">
          <span className="content-base-low group-hover:content-action-brand">{taskCode}</span> {task.title}
        </p>

        <div className="mt-auto flex flex-col gap-2">
          {task.tags && task.tags.length > 0 && (
            <div className="flex h-5 flex-wrap overflow-hidden content-end gap-1">
              {task.tags.map((item) => (
                <div
                  key={item.text}
                  className={cn("text-label-xs px-1.5 py-0.5 radius-xl h-fit", getTagClasses(item.color))}>
                  {item.text}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center justify-start gap-1">
              {task.assigned_to_name && (
                <div className="relative group/avatar">
                  <Avatar name={task.assigned_to_name} size={18} textClassName="text-[8px]" />
                  <div className="absolute hidden group-hover/avatar:block bottom-full left-1/2 -translate-x-1/2 mb-2.5 px-2 py-1 radius-xs surface-base-fill border surface-component-stroke text-label-xs content-base-primary whitespace-nowrap z-10">
                    {task.assigned_to_name}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-start">
                <div className="py-0.5 flex justify-center items-center gap-1">
                  <span
                    className={cn("w-1 h-1 rounded-full", PRIORITY_COLOR_CLASS[task.priority])}
                    style={{ backgroundColor: "currentColor" }}></span>
                  <p className={cn("text-label-xs", PRIORITY_COLOR_CLASS[task.priority])}>
                    {t(PRIORITY_MAP[task.priority].label)}
                  </p>
                </div>
                <div className="py-0.5 px-1 flex justify-center items-center">
                  <div className={cn("w-4 h-4 flex justify-center items-center", dueDateColorClass)}>
                    <Calendar size={12} color="currentColor" />
                  </div>
                  <p className={cn("text-label-xs", dueDateColorClass)}>{dueDateDisplay.text}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end items-center gap-1">
              {task.requires_fsm_report && (
                <div
                  className={cn(
                    "flex h-4 justify-center items-center gap-1",
                    task.fsm_submission_status === "approved"
                      ? "content-action-positive"
                      : task.fsm_submission_status === "pending_review"
                        ? "content-action-notice"
                        : "content-base-low",
                  )}>
                  <Camera size={12} color="currentColor" variant={task.fsm_submission_status ? "Bold" : "Linear"} />
                </div>
              )}

              {task.comment_count > 0 && (
                <div className="flex h-5 justify-center items-center gap-1 content-base-low">
                  <Message2 size={12} color="currentColor" />
                  <p className="text-label-xs">{task.comment_count}</p>
                </div>
              )}

              {task.task_type === "first_level_task" && task.parent_task !== null && (
                <div className="flex h-4 justify-center items-center gap-1 content-action-negative">
                  <Flag size={12} color="currentColor" variant="Bold" />
                </div>
              )}

              {task.is_favorite && (
                <div className="flex h-4 justify-center items-center gap-1 viz-categorical-01-text">
                  <Star1 size={12} variant="Bold" color="currentColor" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
